import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../authContext';
import { theme } from '../lib/theme';

export default function MarketplaceDriverDash() {
  const { profile, signOutUser, refresh } = useAuth();
  const navigate = useNavigate();
  const [driverProfile, setDriverProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!profile?.id) return;
    setLoading(true);
    setError('');
    const { data, error: loadErr } = await supabase
      .from('driver_profiles')
      .select('status, car_make, car_model, plate_number, has_own_car')
      .eq('id', profile.id)
      .maybeSingle();
    if (loadErr) {
      setError(loadErr.message);
      setDriverProfile(null);
    } else {
      setDriverProfile(data);
    }
    setLoading(false);
  }, [profile?.id]);

  useEffect(() => {
    load();
  }, [load]);

  async function toggleOnline() {
    if (!profile?.id || !driverProfile) return;
    setBusy(true);
    setError('');
    const nextStatus = driverProfile.status === 'online' ? 'offline' : 'online';
    const { error: updateErr } = await supabase
      .from('driver_profiles')
      .update({ status: nextStatus })
      .eq('id', profile.id);
    if (updateErr) {
      setError(updateErr.message);
    } else {
      setDriverProfile((d) => ({ ...d, status: nextStatus }));
    }
    setBusy(false);
  }

  const online = driverProfile?.status === 'online';

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, color: theme.text, fontFamily: theme.font }}>
      <header style={{ padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Link to="/" style={{ color: theme.text, textDecoration: 'none', fontWeight: 700 }}>Transport Team</Link>
        <button
          type="button"
          onClick={async () => {
            await signOutUser();
            navigate('/');
          }}
          style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', color: theme.muted, padding: '8px 14px', borderRadius: 8, cursor: 'pointer' }}
        >
          Sign out
        </button>
      </header>
      <main style={{ maxWidth: 720, margin: '0 auto', padding: '32px 24px' }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>Driver dashboard</h1>
        <p style={{ color: theme.muted, marginBottom: 24 }}>Hello, {profile?.full_name || 'driver'}</p>

        {loading ? (
          <p style={{ color: theme.muted }}>Loading…</p>
        ) : (
          <>
            {driverProfile ? (
              <div style={{ background: theme.bg1, border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: 20, marginBottom: 20 }}>
                <p style={{ margin: '0 0 8px', fontSize: 14, color: theme.muted }}>Vehicle</p>
                <p style={{ margin: 0, fontWeight: 600 }}>
                  {[driverProfile.car_make, driverProfile.car_model].filter(Boolean).join(' ') || 'Not set'}
                  {driverProfile.plate_number ? ` · ${driverProfile.plate_number}` : ''}
                </p>
              </div>
            ) : null}

            <button
              type="button"
              disabled={busy || !driverProfile}
              onClick={toggleOnline}
              style={{
                width: '100%',
                padding: 16,
                borderRadius: 12,
                border: 'none',
                background: online ? 'rgba(248,113,113,0.2)' : theme.accent,
                color: online ? '#f87171' : '#fff',
                fontWeight: 700,
                fontSize: 16,
                cursor: busy || !driverProfile ? 'wait' : 'pointer',
              }}
            >
              {busy ? 'Updating…' : online ? 'Go offline' : 'Go online'}
            </button>
            <p style={{ marginTop: 12, fontSize: 14, color: theme.muted }}>
              Status: <strong style={{ color: theme.text }}>{driverProfile?.status || 'offline'}</strong>
            </p>
          </>
        )}

        {error ? <p style={{ color: '#f87171', marginTop: 16 }}>{error}</p> : null}

        <p style={{ marginTop: 32, fontSize: 13, color: theme.dim }}>
          <button
            type="button"
            onClick={() => refresh()}
            style={{ background: 'none', border: 'none', color: theme.muted, cursor: 'pointer', padding: 0, fontSize: 13 }}
          >
            Refresh profile
          </button>
        </p>
      </main>
    </div>
  );
}
